import { useChatSubmit } from './useChatSubmit';
import { fillIdPlaceholders } from '../helpers';
import type { SuggestedAction } from '../types';

interface Args {
  /** Every row's id, in render order. */
  allIds: string[];
  /** Ticked ids. Empty = the action targets the whole list. */
  selectedIds: string[];
  /** id → display name, for {first_name}/{recipients_phrase}. */
  nameById?: Record<string, string>;
  appIds?: string[];
  /** When set, a bare {first_id} renders as the target's name plus a hidden
   *  <ctx:ctxKey=id> marker — the bubble shows the name, the agent still gets
   *  the id. Job followups use this so "job 21480" reads as the job title. */
  idAsName?: { ctxKey: string; fallbackLabel: string };
}

// The follow-up chip contract every block shares: the selection drives the
// action (falling back to the whole list), and multi-target chips show the
// narrowed count.
export const useBlockFollowup = ({
  allIds,
  selectedIds,
  nameById,
  appIds,
  idAsName,
}: Args) => {
  const { send, isSending } = useChatSubmit();
  const ids = selectedIds.length > 0 ? selectedIds : allIds;

  const sendFollowup = (template: string) => {
    if (isSending) return;
    const names = ids.map((id) => nameById?.[id] ?? '');
    void send(
      fillIdPlaceholders(
        template,
        ids,
        appIds,
        names,
        idAsName ? { idAsCtxName: idAsName } : undefined
      )
    );
  };

  const withCounts = (actions: SuggestedAction[] = []): SuggestedAction[] =>
    selectedIds.length === 0
      ? actions
      : actions.map((a) =>
          a.promptTemplate.includes('{ids}') ||
          a.promptTemplate.includes('{count}')
            ? { ...a, label: `${a.label} (${selectedIds.length})` }
            : a
        );

  return { sendFollowup, withCounts, isSending };
};
